import { useMemo, useCallback, useRef } from 'react';
import { Stage, Layer, Rect, Group, Text } from 'react-konva';
import type Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { useEditorStore } from '@/stores/editor-store';
import type { Selection } from '@/stores/editor-store';
import {
  calculateBeadPositions,
  calculateGridSize,
  createRenderConfig,
  hitTestBead,
} from '@/lib/grid-renderer';
import type { BeadPosition } from '@/lib/grid-renderer';

interface BeadGridProps {
  readonly width: number;
  readonly height: number;
}

const CELL_SIZE = 20;
const GAP = 1;
const ZOOM_FACTOR = 1.1;
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 10;

const GRID_LINE_COLOR = '#d1d5db';
const EMPTY_BEAD_COLOR = '#f9fafb';
const EMPTY_BEAD_STROKE = '#e5e7eb';
const PREVIEW_EMPTY_COLOR = '#ffffff';
const SELECTION_STROKE = '#6366f1';
const PASTE_GHOST_OPACITY = 0.5;
const ROW_LABEL_OFFSET = -28;
const ROW_LABEL_FONT_SIZE = 10;

export function BeadGrid({ width, height }: BeadGridProps) {
  const stageRef = useRef<Konva.Stage>(null);

  // Store selectors
  const technique = useEditorStore((s) => s.technique);
  const gridWidth = useEditorStore((s) => s.gridWidth);
  const gridHeight = useEditorStore((s) => s.gridHeight);
  const grid = useEditorStore((s) => s.grid);
  const palette = useEditorStore((s) => s.palette);
  const zoom = useEditorStore((s) => s.zoom);
  const panX = useEditorStore((s) => s.panX);
  const panY = useEditorStore((s) => s.panY);
  const showGrid = useEditorStore((s) => s.showGrid);
  const previewMode = useEditorStore((s) => s.previewMode);
  const activeTool = useEditorStore((s) => s.activeTool);
  const activeColorIndex = useEditorStore((s) => s.activeColorIndex);
  const selection = useEditorStore((s) => s.selection);
  const isPasting = useEditorStore((s) => s.isPasting);
  const clipboard = useEditorStore((s) => s.clipboard);
  const pasteRow = useEditorStore((s) => s.pasteRow);
  const pasteCol = useEditorStore((s) => s.pasteCol);

  const setCell = useEditorStore((s) => s.setCell);
  const floodFillAt = useEditorStore((s) => s.floodFillAt);
  const eyedrop = useEditorStore((s) => s.eyedrop);
  const pushUndo = useEditorStore((s) => s.pushUndo);
  const setZoom = useEditorStore((s) => s.setZoom);
  const setPan = useEditorStore((s) => s.setPan);
  const setSelection = useEditorStore((s) => s.setSelection);
  const movePaste = useEditorStore((s) => s.movePaste);
  const confirmPaste = useEditorStore((s) => s.confirmPaste);

  // ─── Grid geometry ─────────────────────────────────

  const renderConfig = useMemo(
    () => createRenderConfig(technique, gridWidth, gridHeight, CELL_SIZE, GAP),
    [technique, gridWidth, gridHeight],
  );
  const beadPositions = useMemo(() => calculateBeadPositions(renderConfig), [renderConfig]);
  const gridSize = useMemo(() => calculateGridSize(renderConfig), [renderConfig]);

  const groupX = (width - gridSize.width * zoom) / 2 + panX;
  const groupY = (height - gridSize.height * zoom) / 2 + panY;

  // ─── Color lookup ──────────────────────────────────

  const getBeadColor = useCallback(
    (row: number, col: number): string => {
      const rowData = grid[row];
      if (!rowData) return previewMode ? PREVIEW_EMPTY_COLOR : EMPTY_BEAD_COLOR;
      const idx = rowData[col];
      if (idx === undefined || idx === 0)
        return previewMode ? PREVIEW_EMPTY_COLOR : EMPTY_BEAD_COLOR;
      const color = palette[idx - 1];
      return color?.beadHex ?? (previewMode ? PREVIEW_EMPTY_COLOR : EMPTY_BEAD_COLOR);
    },
    [grid, palette, previewMode],
  );

  const getPaletteHex = useCallback(
    (idx: number): string => {
      if (idx === 0) return EMPTY_BEAD_COLOR;
      return palette[idx - 1]?.beadHex ?? EMPTY_BEAD_COLOR;
    },
    [palette],
  );

  // ─── Hit test ──────────────────────────────────────

  const pointerToGrid = useCallback(
    (stage: Konva.Stage): BeadPosition | null => {
      const pointer = stage.getPointerPosition();
      if (!pointer) return null;
      const gx = (pointer.x - groupX) / zoom;
      const gy = (pointer.y - groupY) / zoom;
      return hitTestBead(gx, gy, beadPositions);
    },
    [beadPositions, groupX, groupY, zoom],
  );

  // ─── Interaction refs ──────────────────────────────

  const isPaintingRef = useRef(false);
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const selStartRef = useRef<{ row: number; col: number } | null>(null);
  const lastPinchDistRef = useRef(0);

  // ─── Mouse handlers ────────────────────────────────

  const handleMouseDown = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      const stage = e.target.getStage();
      if (!stage) return;

      // Right/middle click → pan
      if (e.evt.button === 1 || e.evt.button === 2) {
        e.evt.preventDefault();
        isPanningRef.current = true;
        panStartRef.current = { x: e.evt.clientX, y: e.evt.clientY, panX, panY };
        return;
      }

      if (previewMode) return;
      const pos = pointerToGrid(stage);
      if (!pos) return;

      // Paste mode: click = confirm paste at position
      if (isPasting) {
        pushUndo();
        confirmPaste();
        return;
      }

      switch (activeTool) {
        case 'pencil':
          pushUndo();
          isPaintingRef.current = true;
          if (activeColorIndex > 0) setCell(pos.row, pos.col, activeColorIndex);
          break;
        case 'eraser':
          pushUndo();
          isPaintingRef.current = true;
          setCell(pos.row, pos.col, 0);
          break;
        case 'fill':
          pushUndo();
          floodFillAt(pos.row, pos.col, activeColorIndex);
          break;
        case 'eyedropper':
          eyedrop(pos.row, pos.col);
          break;
        case 'select':
          selStartRef.current = { row: pos.row, col: pos.col };
          setSelection({ startRow: pos.row, startCol: pos.col, endRow: pos.row, endCol: pos.col });
          break;
      }
    },
    [activeTool, activeColorIndex, pointerToGrid, panX, panY, previewMode, isPasting, setCell, floodFillAt, eyedrop, setSelection, confirmPaste, pushUndo],
  );

  const handleMouseMove = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      // Panning
      if (isPanningRef.current) {
        const dx = e.evt.clientX - panStartRef.current.x;
        const dy = e.evt.clientY - panStartRef.current.y;
        setPan(panStartRef.current.panX + dx, panStartRef.current.panY + dy);
        return;
      }

      const stage = e.target.getStage();
      if (!stage) return;
      const pos = pointerToGrid(stage);
      if (!pos) return;

      // Paste ghost movement
      if (isPasting) {
        movePaste(pos.row, pos.col);
        return;
      }

      // Painting
      if (isPaintingRef.current) {
        if (activeTool === 'pencil' && activeColorIndex > 0) {
          setCell(pos.row, pos.col, activeColorIndex);
        } else if (activeTool === 'eraser') {
          setCell(pos.row, pos.col, 0);
        }
        return;
      }

      // Selection dragging
      if (selStartRef.current && activeTool === 'select') {
        setSelection({
          startRow: selStartRef.current.row,
          startCol: selStartRef.current.col,
          endRow: pos.row,
          endCol: pos.col,
        });
      }
    },
    [activeTool, activeColorIndex, pointerToGrid, isPasting, setPan, setCell, setSelection, movePaste],
  );

  const handleMouseUp = useCallback(() => {
    isPaintingRef.current = false;
    isPanningRef.current = false;
    selStartRef.current = null;
  }, []);

  // ─── Touch handlers ────────────────────────────────

  const handleTouchStart = useCallback(
    (e: KonvaEventObject<TouchEvent>) => {
      const touches = e.evt.touches;
      if (touches.length === 2) {
        e.evt.preventDefault();
        const dx = touches[1]!.clientX - touches[0]!.clientX;
        const dy = touches[1]!.clientY - touches[0]!.clientY;
        lastPinchDistRef.current = Math.sqrt(dx * dx + dy * dy);
        const cx = (touches[0]!.clientX + touches[1]!.clientX) / 2;
        const cy = (touches[0]!.clientY + touches[1]!.clientY) / 2;
        panStartRef.current = { x: cx, y: cy, panX, panY };
        return;
      }

      if (touches.length === 1 && !previewMode) {
        const stage = e.target.getStage();
        if (!stage) return;
        const pos = pointerToGrid(stage);
        if (!pos) return;

        if (isPasting) {
          confirmPaste();
          return;
        }

        if (activeTool === 'pencil' || activeTool === 'eraser') {
          isPaintingRef.current = true;
          if (activeTool === 'pencil' && activeColorIndex > 0) setCell(pos.row, pos.col, activeColorIndex);
          else if (activeTool === 'eraser') setCell(pos.row, pos.col, 0);
        } else if (activeTool === 'fill') {
          floodFillAt(pos.row, pos.col, activeColorIndex);
        } else if (activeTool === 'eyedropper') {
          eyedrop(pos.row, pos.col);
        }
      }
    },
    [activeTool, activeColorIndex, pointerToGrid, panX, panY, previewMode, isPasting, setCell, floodFillAt, eyedrop, confirmPaste],
  );

  const handleTouchMove = useCallback(
    (e: KonvaEventObject<TouchEvent>) => {
      const touches = e.evt.touches;
      if (touches.length === 2) {
        e.evt.preventDefault();
        const dx = touches[1]!.clientX - touches[0]!.clientX;
        const dy = touches[1]!.clientY - touches[0]!.clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const cx = (touches[0]!.clientX + touches[1]!.clientX) / 2;
        const cy = (touches[0]!.clientY + touches[1]!.clientY) / 2;

        if (lastPinchDistRef.current > 0) {
          const scale = dist / lastPinchDistRef.current;
          setZoom(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom * scale)));
        }
        lastPinchDistRef.current = dist;

        const panDx = cx - panStartRef.current.x;
        const panDy = cy - panStartRef.current.y;
        setPan(panStartRef.current.panX + panDx, panStartRef.current.panY + panDy);
        return;
      }

      if (isPaintingRef.current) {
        const stage = e.target.getStage();
        if (!stage) return;
        const pos = pointerToGrid(stage);
        if (!pos) return;
        if (activeTool === 'pencil' && activeColorIndex > 0) setCell(pos.row, pos.col, activeColorIndex);
        else if (activeTool === 'eraser') setCell(pos.row, pos.col, 0);
      }
    },
    [activeTool, activeColorIndex, zoom, pointerToGrid, setZoom, setPan, setCell],
  );

  const handleTouchEnd = useCallback(() => {
    isPaintingRef.current = false;
    lastPinchDistRef.current = 0;
  }, []);

  // ─── Scroll wheel zoom ─────────────────────────────

  const handleWheel = useCallback(
    (e: KonvaEventObject<WheelEvent>) => {
      e.evt.preventDefault();
      const stage = stageRef.current;
      if (!stage) return;
      const pointer = stage.getPointerPosition();
      if (!pointer) return;

      const direction = e.evt.deltaY < 0 ? 1 : -1;
      const newZoom = Math.max(
        MIN_ZOOM,
        Math.min(MAX_ZOOM, direction > 0 ? zoom * ZOOM_FACTOR : zoom / ZOOM_FACTOR),
      );
      const zoomRatio = newZoom / zoom;
      const newPanX = pointer.x - zoomRatio * (pointer.x - panX - (width - gridSize.width * zoom) / 2) - (width - gridSize.width * newZoom) / 2;
      const newPanY = pointer.y - zoomRatio * (pointer.y - panY - (height - gridSize.height * zoom) / 2) - (height - gridSize.height * newZoom) / 2;
      setZoom(newZoom);
      setPan(newPanX, newPanY);
    },
    [zoom, panX, panY, width, height, gridSize, setZoom, setPan],
  );

  // ─── Cursor ────────────────────────────────────────

  const cursor = useMemo(() => {
    if (previewMode) return 'default';
    if (isPasting) return 'copy';
    switch (activeTool) {
      case 'pencil': return 'crosshair';
      case 'eraser': return 'crosshair';
      case 'fill': return 'crosshair';
      case 'eyedropper': return 'crosshair';
      case 'select': return 'crosshair';
      default: return 'default';
    }
  }, [activeTool, previewMode, isPasting]);

  // ─── Selection highlight helper ────────────────────

  const normalizedSelection = useMemo((): Selection | null => {
    if (!selection) return null;
    return {
      startRow: Math.min(selection.startRow, selection.endRow),
      startCol: Math.min(selection.startCol, selection.endCol),
      endRow: Math.max(selection.startRow, selection.endRow),
      endCol: Math.max(selection.startCol, selection.endCol),
    };
  }, [selection]);

  const isSelected = useCallback(
    (row: number, col: number): boolean => {
      if (!normalizedSelection) return false;
      return (
        row >= normalizedSelection.startRow &&
        row <= normalizedSelection.endRow &&
        col >= normalizedSelection.startCol &&
        col <= normalizedSelection.endCol
      );
    },
    [normalizedSelection],
  );

  // ─── Bead position lookup for paste ghost ──────────

  const beadPosMap = useMemo(() => {
    const map = new Map<string, BeadPosition>();
    for (const pos of beadPositions) {
      map.set(`${pos.row},${pos.col}`, pos);
    }
    return map;
  }, [beadPositions]);

  // ─── Row labels ───────────────────────────────────

  const rowLabels = useMemo(() => {
    if (previewMode) return [];
    // Show every row when zoomed in, every 5th when zoomed out
    const step = zoom < 0.4 ? 10 : zoom < 0.7 ? 5 : 1;
    const labels: { row: number; y: number }[] = [];
    for (let r = 0; r < gridHeight; r++) {
      if (r % step !== 0) continue;
      const pos = beadPositions[r * gridWidth];
      if (pos) {
        labels.push({ row: r + 1, y: pos.y + pos.height / 2 });
      }
    }
    return labels;
  }, [beadPositions, gridWidth, gridHeight, zoom, previewMode]);

  // ─── Render ────────────────────────────────────────

  const showStrokes = showGrid && !previewMode;

  return (
    <Stage
      ref={stageRef}
      width={width}
      height={height}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
      onContextMenu={(e) => e.evt.preventDefault()}
      style={{ cursor }}
    >
      <Layer>
        <Group x={groupX} y={groupY} scaleX={zoom} scaleY={zoom}>
          {/* Beads */}
          {beadPositions.map((pos) => {
            const color = getBeadColor(pos.row, pos.col);
            const isEmpty = color === EMPTY_BEAD_COLOR || color === PREVIEW_EMPTY_COLOR;
            const selected = isSelected(pos.row, pos.col);

            return (
              <Rect
                key={`${pos.row}-${pos.col}`}
                x={pos.x}
                y={pos.y}
                width={pos.width}
                height={pos.height}
                fill={color}
                stroke={
                  selected
                    ? SELECTION_STROKE
                    : showStrokes
                      ? isEmpty
                        ? EMPTY_BEAD_STROKE
                        : GRID_LINE_COLOR
                      : undefined
                }
                strokeWidth={selected ? 1.5 : showStrokes ? 0.5 : 0}
                cornerRadius={previewMode ? 0 : 2}
              />
            );
          })}

          {/* Row labels */}
          {rowLabels.map((label) => (
            <Text
              key={`row-${label.row}`}
              x={ROW_LABEL_OFFSET}
              y={label.y - ROW_LABEL_FONT_SIZE / 2}
              text={String(label.row)}
              fontSize={ROW_LABEL_FONT_SIZE}
              fontFamily="monospace"
              fill="#9ca3af"
              align="right"
              width={24}
            />
          ))}

          {/* Paste ghost overlay */}
          {isPasting &&
            clipboard &&
            clipboard.data.map((row, r) =>
              row.map((val, c) => {
                if (val === 0) return null;
                const targetPos = beadPosMap.get(`${pasteRow + r},${pasteCol + c}`);
                if (!targetPos) return null;
                return (
                  <Rect
                    key={`paste-${r}-${c}`}
                    x={targetPos.x}
                    y={targetPos.y}
                    width={targetPos.width}
                    height={targetPos.height}
                    fill={getPaletteHex(val)}
                    opacity={PASTE_GHOST_OPACITY}
                    stroke={SELECTION_STROKE}
                    strokeWidth={1}
                    cornerRadius={2}
                  />
                );
              }),
            )}
        </Group>
      </Layer>
    </Stage>
  );
}

/** Calculate zoom level to fit the grid in the viewport. */
export function calculateFitZoom(
  viewportWidth: number,
  viewportHeight: number,
  gridPixelWidth: number,
  gridPixelHeight: number,
  padding = 40,
): number {
  const availW = viewportWidth - padding * 2;
  const availH = viewportHeight - padding * 2;
  if (availW <= 0 || availH <= 0) return 1;
  return Math.min(availW / gridPixelWidth, availH / gridPixelHeight, MAX_ZOOM);
}
